import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { UncontrolledTooltip } from "reactstrap";
import s from "./styles.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faPlus,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { Localidad } from "../../../types/codigoPostal";

export interface Props {
  page:
    | {
        content: Localidad[];
        totalElements: number;
        totalPages: number;
        size: number;
        number: number;
        first: boolean;
        last: boolean;
      }
    | Localidad[];
  onAddClick: () => void;
  onEditClick: (localidad: Localidad) => void;
  onDeleteClick: (id: number) => void;
  onPageChange?: (page: number, size: number) => void;
}

export const CodigoPostalTable = React.memo(
  ({ page, onAddClick, onEditClick, onDeleteClick, onPageChange }: Props) => {
    const [columns] = useState(
      columnsFormatter(onAddClick, onEditClick, onDeleteClick)
    );

    const isPagedResponse =
      page && typeof page === "object" && "content" in page;

    const pageData = isPagedResponse
      ? page.content
      : Array.isArray(page)
      ? page
      : [];
    const totalElements = isPagedResponse
      ? page.totalElements
      : pageData.length;
    const totalPages = isPagedResponse
      ? page.totalPages
      : Math.ceil(pageData.length / 20);
    const currentPage = isPagedResponse ? page.number : 0;
    const pageSize = isPagedResponse ? page.size : 20;

    const handlePageChange = (pageIndex: number) => {
      if (onPageChange && isPagedResponse) {
        onPageChange(pageIndex, pageSize);
      }
    };

    const handlePageSizeChange = (newPageSize: number) => {
      if (onPageChange && isPagedResponse) {
        onPageChange(0, newPageSize);
      }
    };

    return (
      <>
        <div className="row mt-2">
          <div style={{ width: "100%" }}>
            <ReactTable
              manual={isPagedResponse}
              data={pageData}
              columns={columns}
              loading={false}
              pageSize={pageSize}
              className="-striped -highlight"
              page={currentPage}
              pages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showPagination={totalElements > pageSize}
              showPageSizeOptions={true}
              pageSizeOptions={[10, 20, 50, 100]}
              getTrProps={() => ({ style: { height: "30px" } })}
              sortable={false}
            />
          </div>
        </div>

        {isPagedResponse && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Mostrando {pageData.length} de {totalElements} registros
            </small>
            <small className="text-muted">
              Página {currentPage + 1} de {totalPages}
            </small>
          </div>
        )}
      </>
    );
  }
);

function columnsFormatter(
  onAddClick: () => void,
  onEditClick: (localidad: Localidad) => void,
  onDeleteClick: (id: number) => void
) {
  return [
    {
      Header: "Localidad ID",
      id: "localidadId",
      className: s.tdClear,
      accessor: simpleAccessor("localidadId")
    },
    {
      Header: "Codigo Postal",
      id: "codigoPostal",
      className: s.tdClear,
      accessor: simpleAccessor("codigoPostal")
    },
    {
      Header: "Estado",
      id: "estado",
      className: s.tdClear,
      accessor: simpleAccessor("estado")
    },
    {
      Header: "Municipio",
      id: "municipio",
      className: s.tdClear,
      accessor: simpleAccessor("municipio")
    },
    {
      Header: "Localidad",
      id: "localidad",
      className: s.tdClear,
      accessor: simpleAccessor("nombre")
    },
    {
      Header: () => (
        <div className="w-100 text-center">
          <span id="productoAddId" className={s.btn} onClick={onAddClick}>
            <FontAwesomeIcon icon={faPlus} />
            <UncontrolledTooltip placement="left" target="productoAddId">
              Crear Localidad
            </UncontrolledTooltip>
          </span>
        </div>
      ),
      id: "acciones",
      accessor: (row: Localidad) => (
        <div className={`text-center`}>
          <span
            id="localidadEditId"
            className={s.btn}
            onClick={() => onEditClick(row)}
          >
            <FontAwesomeIcon icon={faPencilAlt} />
            <UncontrolledTooltip placement="left" target="localidadEditId">
              Editar Localidad
            </UncontrolledTooltip>
          </span>
          <span
            id="localidadDeleteId"
            className={s.btn}
            onClick={() => onDeleteClick(row.localidadId)}
          >
            <FontAwesomeIcon icon={faTrash} />
            <UncontrolledTooltip placement="right" target="localidadDeleteId">
              Borrar Localidad
            </UncontrolledTooltip>
          </span>
        </div>
      )
    }
  ];
}

function simpleAccessor(key: keyof Localidad) {
  return (row: Localidad) => {
    return <div className={s.field}>{row[key]}&nbsp;</div>;
  };
}
